import React, { useState, useEffect, useContext, useRef, Component, memo, useCallback } from 'react';
import docsApiClient from '@/api/docsClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { UploadCloud, FileText, Trash2, Filter, Grid, List, Download, Loader2, Brain, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { VirtualList, VirtualGrid } from '@/components/VirtualList';
import { useLanguage, translations } from '@/contexts/LanguageContext.jsx';

// Threshold for enabling virtual scrolling
const VIRTUAL_SCROLL_THRESHOLD = 20;

// Error Boundary to prevent page crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('DocumentsPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 bg-slate-950 min-h-full flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Something went wrong</h2>
            <p className="text-slate-400 mb-4">Please refresh the page to try again</p>
            <Button onClick={() => window.location.reload()} className="bg-indigo-600 hover:bg-indigo-700">
              <RefreshCw size={16} className="mr-2" />
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Memoized Grid Card Component for Virtual Scrolling
const DocumentGridCard = memo(({ doc, isSelected, onToggleSelect, onDownload, onDelete }) => (
  <Card 
    data-testid={`document-card-${doc.id}`}
    className={`p-6 bg-slate-900 border-slate-800 hover:bg-slate-800/50 transition-colors ${
      isSelected ? 'ring-2 ring-indigo-500' : ''
    }`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(doc.id)}
          className="border-slate-600"
        />
        <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center">
          <FileText size={20} className="text-indigo-400" />
        </div>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onDownload(doc.id, doc.title, doc.file_path?.split('.').pop())} className="text-slate-400 hover:text-indigo-400">
          <Download size={16} />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(doc.id)} className="text-slate-400 hover:text-red-400">
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">{doc.title}</h3>
    <p className="text-sm text-slate-400 mb-3 line-clamp-2">{doc.description || 'No description'}</p>
    <div className="flex flex-wrap gap-2 mb-3">
      {doc.tags?.slice(0, 3).map((tag, idx) => (
        <span key={idx} className="px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded">
          {tag}
        </span>
      ))}
    </div>
    <div className="pt-3 border-t border-slate-800 text-xs text-slate-500">
      <p>{doc.department || doc.departments?.join(', ')}</p>
      <p className="mt-1">{new Date(doc.created_at).toLocaleDateString()}</p>
      {doc.ai_processing_status === 'processing' && (
        <p className="mt-1 text-amber-400 flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Analyzing...
        </p>
      )}
      {doc.ai_processing_status === 'completed' && (
        <p className="mt-1 text-indigo-400 flex items-center gap-1">
          <Brain size={12} /> AI Ready
        </p>
      )}
    </div>
  </Card>
));

DocumentGridCard.displayName = 'DocumentGridCard';

// Memoized List Item Component for Virtual Scrolling
const DocumentListItem = memo(({ doc, isSelected, onToggleSelect, onDownload, onDelete }) => (
  <Card 
    data-testid={`document-list-item-${doc.id}`}
    className={`p-4 bg-slate-900 border-slate-800 ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}
  >
    <div className="flex items-center gap-4">
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onToggleSelect(doc.id)}
        className="border-slate-600"
      />
      <div className="w-10 h-10 bg-indigo-600/20 rounded-lg flex items-center justify-center">
        <FileText size={20} className="text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-semibold truncate">{doc.title}</h3>
        <p className="text-sm text-slate-400 truncate">{doc.description || 'No description'}</p>
      </div>
      <div className="text-right text-sm text-slate-500 hidden md:block">
        <p>{doc.department}</p>
        <p>{new Date(doc.created_at).toLocaleDateString()}</p>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onDownload(doc.id, doc.title, doc.file_path?.split('.').pop())} className="text-slate-400 hover:text-indigo-400">
          <Download size={16} />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(doc.id)} className="text-slate-400 hover:text-red-400">
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  </Card>
));

DocumentListItem.displayName = 'DocumentListItem';

const DocumentsPageContent = () => {
  const token = localStorage.getItem('token');
  const { language } = useLanguage();
  const t = translations[language];
  const [documents, setDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    department: '',
    tags: '',
    description: '',
    visibility: 'general'
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDocuments();
    loadDepartments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [filterDepartment, searchQuery, allDocuments]);

  const loadDocuments = async () => {
    try {
      const response = await docsApiClient.get('/documents');
      setAllDocuments(response.data);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await docsApiClient.get('/client/departments');
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const filterDocuments = () => {
    let filtered = [...allDocuments];
    if (filterDepartment && filterDepartment !== 'all') {
      filtered = filtered.filter(doc => 
        doc.department === filterDepartment || 
        doc.departments?.includes(filterDepartment)
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.title?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    setDocuments(filtered);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setLoading(true);

    try {
      if (selectedFiles.length > 1) {
        // Bulk upload
        const bulkFormData = new FormData();
        selectedFiles.forEach(file => bulkFormData.append('files', file));
        bulkFormData.append('department', formData.department || '');
        bulkFormData.append('tags', formData.tags || '');
        bulkFormData.append('visibility', formData.visibility || 'general');

        const response = await docsApiClient.post('/documents/upload-bulk', bulkFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000 // 2 minute timeout for large uploads
        });
        
        if (response.data) {
          toast.success(`${selectedFiles.length} documents uploaded! Processing in background.`);
        }
      } else {
        // Single file upload
        const singleFormData = new FormData();
        singleFormData.append('file', selectedFiles[0]);
        singleFormData.append('title', selectedFiles[0].name.replace(/\.[^/.]+$/, ''));
        singleFormData.append('department', formData.department || '');
        singleFormData.append('tags', formData.tags || '');
        singleFormData.append('description', formData.description || '');
        singleFormData.append('visibility', formData.visibility || 'general');

        await docsApiClient.post('/documents/upload', singleFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        toast.success('Document uploaded successfully!');
      }

      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setFormData({ department: '', tags: '', description: '', visibility: 'general' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload documents';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = useCallback(async (docId, title, ext) => {
    try {
      const response = await docsApiClient.get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title}.${ext || 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  }, []);

  const handleDelete = useCallback(async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await docsApiClient.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  }, []);

  const handleFixStuckDocuments = async () => {
    try {
      const response = await docsApiClient.post('/documents/fix-stuck-processing', {});
      toast.success(`Fixed ${response.data.fixed_count} stuck documents`);
      loadDocuments();
    } catch (error) {
      toast.error('Failed to fix stuck documents');
    }
  };

  const hasStuckDocuments = documents.some(doc => {
    if (doc.ai_processing_status !== 'processing') return false;
    const tenMinsAgo = new Date(Date.now() - 10 * 60 * 1000);
    return new Date(doc.created_at) < tenMinsAgo;
  });

  const toggleDocSelection = useCallback((docId) => {
    setSelectedDocIds(prev => 
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  }, []);

  const handleBulkDelete = async () => {
    if (selectedDocIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedDocIds.length} documents?`)) return;
    
    try {
      await Promise.all(selectedDocIds.map(id => 
        docsApiClient.delete(`/documents/${id}`)
      ));
      toast.success(`Deleted ${selectedDocIds.length} documents`);
      setSelectedDocIds([]);
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete some documents');
    }
  };

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">{t.documents}</h1>
          <p className="text-base text-slate-400 mt-2">{t.manageKnowledgeBase}</p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {hasStuckDocuments && (
            <Button
              onClick={handleFixStuckDocuments}
              variant="outline"
              className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
            >
              <AlertTriangle size={16} className="mr-2" />
              Fix Stuck
            </Button>
          )}

          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
                <UploadCloud size={18} className="mr-2" />
                {t.uploadDocuments}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-2xl font-semibold text-white">{t.uploadDocuments}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  {t.selectFiles}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-6 mt-4">
                {/* File Input */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">
                    {t.files} {selectedFiles.length > 0 && `(${selectedFiles.length} selected)`}
                  </Label>
                  <div 
                    className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-indigo-500 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadCloud size={40} className="mx-auto text-slate-500 mb-3" />
                    <p className="text-slate-300 font-medium">{t.clickToSelect}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {t.supportedFormats}
                    </p>
                    <p className="text-xs text-indigo-400 mt-2">
                      {t.holdCtrlMultiple}
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {/* Selected files preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-32 overflow-y-auto">
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-800 rounded px-3 py-2">
                          <div className="flex items-center gap-2">
                            <FileText size={16} className="text-indigo-400" />
                            <span className="text-sm text-slate-300 truncate max-w-[200px]">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                            }}
                            className="text-slate-500 hover:text-red-400"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Department */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">{t.department}</Label>
                  <Select value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder={t.selectDepartment} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept} className="text-white hover:bg-slate-700">
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">{t.tags}</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    placeholder={t.tagsPlaceholder}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>

                {/* Description (only for single file) */}
                {selectedFiles.length <= 1 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-300">{t.description}</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder={t.descriptionPlaceholder}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                )}

                {/* Visibility */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">{t.visibility}</Label>
                  <Select value={formData.visibility} onValueChange={(value) => setFormData({...formData, visibility: value})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="general" className="text-white">{t.generalAllUsers}</SelectItem>
                      <SelectItem value="department" className="text-white">{t.departmentOnly}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || selectedFiles.length === 0}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      {t.uploading}
                    </>
                  ) : (
                    <>
                      <UploadCloud size={18} className="mr-2" />
                      {selectedFiles.length > 1 ? `${t.uploadFiles} (${selectedFiles.length})` : t.uploadFile}
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-slate-400" />
          <Select value={filterDepartment} onValueChange={setFilterDepartment}>
            <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder={t.filterAllDepartments} />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">{t.filterAllDepartments}</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept} className="text-white">{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Input
          placeholder={t.searchDocuments}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-64 bg-slate-900 border-slate-700 text-white"
        />

        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? 'bg-indigo-600' : 'text-slate-400'}
          >
            <Grid size={18} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'bg-indigo-600' : 'text-slate-400'}
          >
            <List size={18} />
          </Button>
        </div>

        {selectedDocIds.length > 0 && (
          <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
            <Trash2 size={16} className="mr-2" />
            {t.delete} ({selectedDocIds.length})
          </Button>
        )}
      </div>

      {/* Documents Grid/List */}
      {documents.length === 0 ? (
        <div className="text-center py-16">
          <FileText size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t.noDocumentsFound}</h3>
          <p className="text-slate-400">{t.uploadFirstDocument}</p>
        </div>
      ) : viewMode === 'grid' ? (
        documents.length > VIRTUAL_SCROLL_THRESHOLD ? (
          <VirtualGrid
            items={documents}
            columns={3}
            itemHeight={280}
            gap={24}
            className="h-[calc(100vh-320px)]"
            renderItem={(doc) => (
              <DocumentGridCard
                doc={doc}
                isSelected={selectedDocIds.includes(doc.id)}
                onToggleSelect={toggleDocSelection}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            )}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map(doc => (
              <DocumentGridCard
                key={doc.id}
                doc={doc}
                isSelected={selectedDocIds.includes(doc.id)}
                onToggleSelect={toggleDocSelection}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )
      ) : (
        documents.length > VIRTUAL_SCROLL_THRESHOLD ? (
          <VirtualList
            items={documents}
            itemHeight={80}
            className="h-[calc(100vh-320px)]"
            renderItem={(doc) => (
              <div className="mb-3">
                <DocumentListItem
                  doc={doc}
                  isSelected={selectedDocIds.includes(doc.id)}
                  onToggleSelect={toggleDocSelection}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                />
              </div>
            )}
          />
        ) : (
          <div className="space-y-3">
            {documents.map(doc => (
              <DocumentListItem
                key={doc.id}
                doc={doc}
                isSelected={selectedDocIds.includes(doc.id)}
                onToggleSelect={toggleDocSelection}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

// Wrap with ErrorBoundary
export const DocumentsPage = () => (
  <ErrorBoundary>
    <DocumentsPageContent />
  </ErrorBoundary>
);

export default DocumentsPage;
